import * as _ from 'lodash-es';
import { Delta, Op } from 'quill/core';
import VersionChangeFormat from '../../editor/formats/VersionChange';
import type { TableChange, TableChangeCell } from '../../editor/table/types';

const getUniqueId = () => _.uniqueId('version-change-');

export type EditVersionChange = {
  id: string;
  type: 'edit';
  isFromPreviousVersion: boolean;
  authorId: string;
};

/**
 * A version change is defined as a series of consecutive changes made by the same author.
 * Only "inserted" and "deleted" are taken into account, whereas "formatted" changes are ignored.
 */
const calculateVersionChanges = (
  diff: Delta,
  topLevelVersionChanges: EditVersionChange[] = [],
) => {
  const versionChanges: EditVersionChange[] = [];
  let delta = new Delta();

  let current: EditVersionChange | null = null;

  diff.forEach((op) => {
    const length = Op.length(op);
    let opDelta = new Delta().retain(length);

    const { diff: diffAttr, author: authorId } = op.attributes || {};
    if (typeof authorId === 'string') {
      if (diffAttr === 'inserted' || diffAttr === 'deleted') {
        if (current?.authorId !== authorId || current?.type !== 'edit') {
          const versionChange: EditVersionChange = {
            id: getUniqueId(),
            type: 'edit',
            isFromPreviousVersion: false,
            authorId,
          };
          versionChanges.push(versionChange);
          current = versionChange;
        }
        opDelta = opDelta.compose(
          new Delta().retain(length, {
            [VersionChangeFormat.attrName]: current.id,
          }),
        );
      } else {
        if (diffAttr !== 'formatted' || op.insert !== '\n') {
          let previousVersionChange = [
            ...versionChanges,
            ...topLevelVersionChanges,
          ].find((v) => v.isFromPreviousVersion && v.authorId === authorId);

          if (!previousVersionChange) {
            previousVersionChange = {
              id: getUniqueId(),
              type: 'edit',
              isFromPreviousVersion: true,
              authorId,
            };
            versionChanges.push(previousVersionChange);
          }
          opDelta = opDelta.compose(
            new Delta().retain(length, {
              [VersionChangeFormat.attrName]: previousVersionChange.id,
            }),
          );
        }

        // This enables that changes of the same author in the same line stay connected
        if (typeof op.insert !== 'string' || op.insert?.includes('\n')) {
          current = null;
        }
      }
    } else {
      current = null;
    }

    const data = op.insert || op.retain;
    if (data && typeof data === 'object') {
      if ('code-embed' in data) {
        const { delta: subDelta, versionChanges: subVersionChanges } =
          calculateVersionChanges(data['code-embed'] as Delta, [
            ...topLevelVersionChanges,
            ...versionChanges,
          ]);
        versionChanges.push(...subVersionChanges);
        opDelta = opDelta.compose(
          new Delta().retain({ 'code-embed': subDelta.ops }),
        );
      } else if ('table-embed' in data) {
        const cellChanges: Record<string, TableChangeCell> = {};
        const { cells } = data['table-embed'] as TableChange;
        if (cells && typeof cells === 'object') {
          Object.keys(cells).forEach((identity) => {
            const content = cells[identity]?.content;
            if (Array.isArray(content)) {
              const { delta: subDelta, versionChanges: subVersionChanges } =
                calculateVersionChanges(new Delta(content), [
                  ...topLevelVersionChanges,
                  ...versionChanges,
                ]);
              versionChanges.push(...subVersionChanges);
              cellChanges[identity] = { content: subDelta.ops };
            }
          });
        }
        if (Object.keys(cellChanges).length) {
          opDelta = opDelta.compose(
            new Delta().retain({ 'table-embed': { cells: cellChanges } }),
          );
        }
      }
    }

    delta = delta.concat(opDelta);
  });

  return {
    delta: delta.chop(),
    versionChanges,
  };
};

export default calculateVersionChanges;
